<script lang="ts">
export default {
  name: "AdminApp",
};
</script>

<script lang="ts" setup>
import AppModal from "@/shared/components/AppModal/index.vue";
import OffCanvas from "@/shared/components/OffCanvas/index.vue";
import AppNavigationHeader from "@/shared/components/AppNavigation/Header/index.vue";
import AppFooter from "@/shared/components/AppFooter/index.vue";
import AppEnvironment from "@/shared/components/AppEnvironment/index.vue";
import BackgroundImage from "@/shared/components/BackgroundImage/index.vue";
import AdminNavigation from "@/admin/components/Navigation/index.vue";
import AdminNavigationMobile from "@/admin/components/Navigation/Mobile/index.vue";
import AppNotifications from "@/shared/components/AppNotifications/index.vue";
import AppConfirm from "@/shared/components/AppConfirm/index.vue";
import SecurePage from "@/admin/components/SecurePage/index.vue";
import AccessCheck from "@/admin/components/AccessCheck.vue";
import { useI18nStore } from "@/shared/stores/i18n";
import { useAppStore } from "@/admin/stores/app";
import { storeToRefs } from "pinia";
import FetchProgressBar from "@/shared/components/FetchProgressBar/index.vue";
import PullToRefresh from "@/shared/components/PullToRefresh/index.vue";
import { useMobile } from "@/shared/composables/useMobile";
import { useMetaInfo } from "@/shared/composables/useMetaInfo";
import { useI18n } from "@/shared/composables/useI18n";
import { useSessionStore } from "@/admin/stores/session";
import { useNavStore } from "@/shared/stores/nav";
import { useOverlayStore } from "@/shared/stores/overlay";
import { useComlink } from "@/shared/composables/useComlink";
import { useAppNotifications } from "@/shared/composables/useAppNotifications";
import { useImportUpdates } from "@/admin/composables/useImportUpdates";
import { useAdminPresenceUpdates } from "@/admin/composables/useAdminPresenceUpdates";
import { useAxiosInterceptors } from "@/admin/composables/useAxiosInterceptors";
import { useMe as useMeQuery } from "@/services/fyAdminApi";
import { useQueryClient } from "@tanstack/vue-query";

const { t } = useI18n();

useAxiosInterceptors();

useMetaInfo({
  appTitle: t("title.defaultAdmin"),
});

const sessionStore = useSessionStore();

const { isAuthenticated } = storeToRefs(sessionStore);

useImportUpdates(isAuthenticated);
useAdminPresenceUpdates(isAuthenticated);

const route = useRoute();

// Every section needs authentication, so on the login page the sidebar is a
// logo above an empty list and the mobile bar is a burger that opens it. Both
// are dropped rather than rendered empty. Named routes only: an unresolved
// route has no name, and reading that as "not the login page" rendered the
// navigation for a frame and then faded it back out over half a second.
const navigationVisible = computed(
  () => route.name !== undefined && route.name !== "admin-login",
);

const comlink = useComlink();

const i18nStore = useI18nStore();

const { locale } = storeToRefs(i18nStore);

// `/models` and `/models/` are one page but not one key: route records are declared
// with a trailing slash, so a link without one lands on the unslashed path and the
// first `router.replace` a filter makes canonicalises it. Keyed on the raw path, that
// one navigation throws the page away and rebuilds it mid-session.
const pageKey = (viewRoute: { path: string }) =>
  `${locale.value}-${viewRoute.path.replace(/\/$/, "")}`;

const appStore = useAppStore();

const mobile = useMobile();

const queryClient = useQueryClient();

const mainInner = ref<HTMLElement | null>(null);

// Same reason as the frontend's: the admin manifest is `standalone` too, so an
// installed admin has no reload button either. See frontend/App.vue.
const refreshPage = () => queryClient.invalidateQueries();

const navStore = useNavStore();

const { collapsed: navCollapsed } = storeToRefs(navStore);

const overlayStore = useOverlayStore();

const { visible: overlayVisible } = storeToRefs(overlayStore);

const { requestBrowserPermission } = useAppNotifications();

watch(
  () => navCollapsed.value,
  () => {
    setNoScroll();
  },
);

watch(
  () => overlayVisible.value,
  () => {
    setNoScroll();
  },
);

const router = useRouter();

watch(
  () => isAuthenticated.value,
  async () => {
    if (isAuthenticated.value) {
      await requestBrowserPermission();

      await refetchCurrentUser();
    } else if (route.meta.needsAuthentication) {
      await router.push({ name: "admin-login" });
    }
  },
);

const onUserUpdateComlink = ref();

onMounted(async () => {
  setNoScroll();

  if (isAuthenticated.value) {
    await requestBrowserPermission();
  }

  // checkVersion();

  // setInterval(() => {
  //   checkVersion();
  // }, CHECK_VERSION_INTERVAL);

  onUserUpdateComlink.value = comlink.on("user-update", refetchCurrentUser);

  // setupLocale();
});

onUnmounted(() => {
  if (onUserUpdateComlink.value) {
    onUserUpdateComlink.value();
  }
});

const {
  data: user,
  isError: currentUserFailed,
  refetch: refetchCurrentUser,
} = useMeQuery({
  query: {
    enabled: isAuthenticated,
  },
});

watch(
  () => user.value,
  () => {
    if (user.value) {
      sessionStore.currentUser = user.value;
    }
  },
);

// A reload restores `authenticated` from storage but not the user it belongs to, so
// the access check has no privileges to match against until `me` comes back. Denying
// on that gap flashes "access denied" on every page whose route declares an `access`.
const currentUserUnknown = computed(
  () =>
    isAuthenticated.value &&
    !sessionStore.currentUser &&
    !currentUserFailed.value,
);

const setNoScroll = () => {
  if (!navCollapsed.value) {
    document.body.classList.add("nav-visible");
  } else {
    document.body.classList.remove("nav-visible");
  }

  if (!navCollapsed.value || overlayVisible.value) {
    document.body.classList.add("no-scroll");
  } else {
    document.body.classList.remove("no-scroll");
  }
};
</script>

<template>
  <div
    :key="locale"
    :class="{
      [`page-${String(route.name)}`]: true,
    }"
    class="app-body"
  >
    <FetchProgressBar />
    <PullToRefresh
      v-if="mobile"
      :target="mainInner"
      :refresh="refreshPage"
      :disabled="!navCollapsed || overlayVisible"
    />
    <BackgroundImage />

    <div class="app-content">
      <transition name="nav-panel">
        <AdminNavigation v-if="navigationVisible" />
      </transition>
      <div class="main-wrapper">
        <div ref="mainInner" class="main-inner">
          <AppNavigationHeader />

          <router-view v-slot="{ Component, route: viewRoute }">
            <AccessCheck
              :resource-access="sessionStore.currentUser?.resourceAccess"
              :super-admin="sessionStore.currentUser?.superAdmin"
              :loading="currentUserUnknown"
            >
              <template #granted>
                <transition name="fade" mode="out-in">
                  <SecurePage
                    v-if="
                      viewRoute.meta.needsSecurityConfirm &&
                      !sessionStore.accessConfirmedDate
                    "
                  />
                  <section
                    v-else
                    class="container main"
                    :class="{
                      [route.name || '']: true,
                    }"
                  >
                    <component :is="Component" :key="pageKey(viewRoute)" />
                  </section>
                </transition>
              </template>
            </AccessCheck>
          </router-view>
        </div>

        <AppFooter
          :version="appStore.version"
          :codename="appStore.codename"
          :git-revision="appStore.gitRevision"
          :online="appStore.online"
        />
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <AdminNavigationMobile v-if="mobile && navigationVisible" />
    </transition>

    <AppConfirm />
    <AppModal />
    <OffCanvas />
    <AppNotifications />
    <AppEnvironment :git-revision="appStore.gitRevision" show-in-production />
  </div>
</template>
